import React, { ReactElement, ReactNode } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { ArrowLeftOutlined, MailOutlined } from '@ant-design/icons';
import { colorsPalette } from '../../../../shared/ui/styles/colors-palette';
import { Loader } from '../../../../shared/ui/ui-kit/elements/Loader';
import { useUILogic } from '../../shared/providers/UILogicProvider/hooks';
import {
  COMPACT_PRODUCT_CODE,
  PRODUCT_TEXT_LOGO,
  type TextLogoProductCode,
} from '../../../infrastructure/types/features/auth/auth-types';
import LoginBackground from '../../shared/assets/LoginBackground.svg';
import LogoFull from '../../shared/assets/LogoFull.svg';
import LogoIcon from '../../shared/assets/LogoIcon.svg';
import BeelineLogo from '../../../../main-service/ui/shared/assets/images/beelineLogo.svg';

interface AuthLayoutProps {
  children: ReactNode;
}

let AuthLayout = function ({ children }: AuthLayoutProps): ReactElement {
  const { t } = useTranslation();
  const { authorizationUiModule, adaptiveUIModule } = useUILogic();
  const product = authorizationUiModule.module.productConfig;
  const websiteUrl = product?.website_url;
  const isCompact = product?.code === COMPACT_PRODUCT_CODE;
  const isMobile = adaptiveUIModule.isMobile;
  const textLogo = product ? PRODUCT_TEXT_LOGO[product.code as TextLogoProductCode] : undefined;

  if (!authorizationUiModule.module.productConfigLoaded) {
    return <Loader centerOfWindow={true} />;
  }

  const logo = isCompact ? (
    <CompactLogoWrapper>
      <CompactLogoImage src={BeelineLogo} alt={product?.name ?? ''} />
      <CompactLogoLabel>{product?.name}</CompactLogoLabel>
    </CompactLogoWrapper>
  ) : textLogo ? (
    <TextLogoWrapper>
      <TextLogoImage src={LogoIcon} alt={textLogo} />
      <TextLogoLabel>{textLogo}</TextLogoLabel>
    </TextLogoWrapper>
  ) : (
    <Logo src={LogoFull} alt="Скорозвон" />
  );

  return (
    <LayoutWrapper $compact={isCompact}>
      <Container $isMobile={isMobile}>
        {!isCompact && websiteUrl && !isMobile && (
          <BackToSite href={websiteUrl}>
            <ArrowLeftOutlined style={{ fontSize: 16 }} />
            <span>{t('common.backToSite')}</span>
          </BackToSite>
        )}
        {isMobile && websiteUrl && <LogoLink href={websiteUrl}>{logo}</LogoLink>}
        {isMobile && !websiteUrl && logo}
        <Content $compact={isCompact} $isMobile={isMobile}>
          {!isMobile && !isCompact && websiteUrl && <LogoLink href={websiteUrl}>{logo}</LogoLink>}
          {!isMobile && isCompact && !websiteUrl && logo}
          {children}
        </Content>
      </Container>
      {!isCompact && product && (
        <Footer $isMobile={isMobile}>
          <Copyright>
            {product.name}, © {new Date().getFullYear()}
          </Copyright>
          {product.email && (
            <MailLink href={`mailto:${product.email}`}>
              <MailOutlined style={{ fontSize: 16, color: 'rgba(0, 0, 0, 0.88)' }} />
              <span>{product.email}</span>
            </MailLink>
          )}
        </Footer>
      )}
    </LayoutWrapper>
  );
};

const LayoutWrapper = styled.div<{ $compact: boolean }>`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: ${({ $compact }) => ($compact ? '#ffffff' : '#f5f7fd')};
  ${({ $compact }) =>
    !$compact &&
    `
    background-image: url(${LoginBackground});
    background-repeat: no-repeat;
    background-size: calc(100% - 254px) calc(100% - 120px);
    background-position: 127px 60px;
  `}
  ${({ $compact }) => $compact && `font-family: 'BeelineSansRegular';`}
`;

const Container = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: ${({ $isMobile }) => ($isMobile ? '345px' : '500px')};
  margin: auto 0;
`;

const BackToSite = styled.a`
  align-self: flex-start;
  margin-bottom: 24px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  line-height: 22px;
  color: ${colorsPalette.gray600};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: ${colorsPalette.gray700};
  }
`;

const Content = styled.div<{ $compact: boolean; $isMobile: boolean }>`
  position: relative;
  width: 100%;
  box-sizing: border-box;
  background: #fff;
  padding: ${({ $compact, $isMobile }) => ($compact ? '64px 48px' : $isMobile ? '24px' : '48px')};
  border: 1px solid #dbdbdb;
  border-radius: 10px;
  box-shadow: 0 0 50px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const LogoLink = styled.a`
  display: block;
  width: fit-content;
`;

const Logo = styled.img`
  width: 150px;
  height: 49px;
  display: block;
`;

const TextLogoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const TextLogoImage = styled.img`
  width: 47px;
  height: 47px;
  display: block;
`;

const TextLogoLabel = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: ${colorsPalette.black};
`;

const CompactLogoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const CompactLogoImage = styled.img`
  width: 19px;
  height: 19px;
  display: block;
`;

const CompactLogoLabel = styled.div`
  font-weight: 500;
  font-size: 24px;
  line-height: 32px;
  color: ${colorsPalette.beelineBlack600};
`;

const Footer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'column-reverse' : 'row')};
  align-items: center;
  gap: ${({ $isMobile }) => ($isMobile ? '12px' : '50px')};
  margin: 0 auto;
  padding-bottom: 6px;
`;

const Copyright = styled.div`
  font-size: 16px;
  line-height: 22px;
  color: ${colorsPalette.gray600};
`;

const MailLink = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;

  span {
    font-size: 16px;
    line-height: 22px;
    color: ${colorsPalette.gray600};
  }
`;

AuthLayout = observer(AuthLayout);

export { AuthLayout };
